import { useState } from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { submitLead } from "@/lib/api";
import { BUILDING_TYPES, COMPANY } from "@/data/content";

const EMPTY = { name: "", company: "", phone: "", email: "", building_type: "", surface: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Merci de renseigner votre nom, téléphone et email.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, lead_type: "contact", source: "contact-form" });
      toast.success("Message envoyé. Notre équipe vous recontacte rapidement.");
      setForm(EMPTY);
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Contact</Overline>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-midnight">
              Parlons de votre projet énergétique
            </h2>
            <p className="mt-5 text-slate-600 font-light leading-relaxed">
              Décrivez votre bâtiment et vos objectifs. Un expert PRIMEC2E vous recontacte pour
              construire une stratégie de performance sur mesure.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex gap-4">
                <div className="h-11 w-11 bg-slate-100 flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-emerald" /></div>
                <div><p className="font-medium text-midnight">Adresse</p><p className="text-sm text-slate-600">{COMPANY.addressFull}</p></div>
              </div>
              <div className="flex gap-4">
                <div className="h-11 w-11 bg-slate-100 flex items-center justify-center shrink-0"><Phone className="h-5 w-5 text-emerald" /></div>
                <div><p className="font-medium text-midnight">Téléphone</p><a href={`tel:${COMPANY.phoneRaw}`} className="text-sm text-slate-600 hover:text-emerald">{COMPANY.phone}</a></div>
              </div>
              <div className="flex gap-4">
                <div className="h-11 w-11 bg-slate-100 flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-emerald" /></div>
                <div><p className="font-medium text-midnight">Email</p><a href={`mailto:${COMPANY.email}`} className="text-sm text-slate-600 hover:text-emerald">{COMPANY.email}</a></div>
              </div>
            </div>

            <div className="mt-8 border border-slate-200 overflow-hidden">
              <iframe
                title="Carte PRIMEC2E La Défense"
                src="https://www.google.com/maps?q=Tour+Coupole+La+Defense+Courbevoie&output=embed"
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="bg-white border border-slate-200 p-8 lg:p-10 shadow-sm space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-name" className="text-xs uppercase tracking-wider text-slate-500">Nom *</Label>
                    <Input id="c-name" data-testid="contact-name" value={form.name} onChange={set("name")} className="rounded-none mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="c-company" className="text-xs uppercase tracking-wider text-slate-500">Société</Label>
                    <Input id="c-company" data-testid="contact-company" value={form.company} onChange={set("company")} className="rounded-none mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="text-xs uppercase tracking-wider text-slate-500">Téléphone *</Label>
                    <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="rounded-none mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
                    <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="rounded-none mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Type de bâtiment</Label>
                    <Select value={form.building_type} onValueChange={set("building_type")}>
                      <SelectTrigger data-testid="contact-building-type" className="rounded-none mt-1.5"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>
                        {BUILDING_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="c-surface" className="text-xs uppercase tracking-wider text-slate-500">Surface (m²)</Label>
                    <Input id="c-surface" data-testid="contact-surface" value={form.surface} onChange={set("surface")} className="rounded-none mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-message" className="text-xs uppercase tracking-wider text-slate-500">Message</Label>
                  <Textarea id="c-message" data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} className="rounded-none mt-1.5" placeholder="Décrivez votre besoin, vos objectifs, vos échéances…" />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit" className="rounded-none bg-emerald hover:bg-emerald-600 text-white font-medium px-8 py-6 group">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <span className="flex items-center gap-2">Envoyer ma demande <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                  )}
                </Button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
